import React from 'react';

import TextInput from 'ui-library/inputs/TextInput';
import {InputChangeEvent} from 'util/Types';

const GENERIC_ERROR = 'Please enter a valid phone number.';
const COUNTRY_CODE_ERROR = 'Non-US numbers must include a country code prefix.';

interface AsyncValidatedPhoneNumberInputProps {
  className?: string;
  onValidInput: (validPhone: string) => void;
  onUpdateErrorMessage: (errorMsg: string) => void;
}
export default function AsyncValidatedPhoneNumberInput({
  className,
  onValidInput,
  onUpdateErrorMessage,
}: AsyncValidatedPhoneNumberInputProps): JSX.Element {
  const [rawNumber, setRawNumber] = React.useState('');
  const [hasError, setHasError] = React.useState(false);

  const onBlur = (): void => {
    // google-libphonenumber is a ~600kb library that is used only here, so we want to exclude it from the
    // main bundles and load it async. The types don't include the Error export for some reason.
    import(/* webpackChunkName: "libphonenumber" */ 'google-libphonenumber').then(
      // @ts-expect-error
      ({PhoneNumberFormat, PhoneNumberUtil, Error: PhoneNumberError}) => {
        const phoneUtil = PhoneNumberUtil.getInstance();
        if (rawNumber.length === 0) {
          setHasError(false);
          onUpdateErrorMessage('');
          return;
        }
        try {
          const parsed = phoneUtil.parse(rawNumber, 'US');
          const isPossible = phoneUtil.isPossibleNumber(parsed);
          const isValid = phoneUtil.isValidNumber(parsed);
          if (isValid) {
            onValidInput(phoneUtil.format(parsed, PhoneNumberFormat.INTERNATIONAL));
            setHasError(false);
            onUpdateErrorMessage('');
          } else if (isPossible) {
            setHasError(true);
            onUpdateErrorMessage(COUNTRY_CODE_ERROR);
          } else {
            setHasError(true);
            onUpdateErrorMessage(GENERIC_ERROR);
          }
        } catch (e) {
          if (Object.values(PhoneNumberError).includes(e.message)) {
            setHasError(true);
            onUpdateErrorMessage(GENERIC_ERROR);
          } else {
            throw e;
          }
        }
      }
    );
  };

  return (
    <>
      <TextInput
        className={className}
        placeholder="Phone Number"
        value={rawNumber}
        onChange={(e: InputChangeEvent) => {
          setRawNumber(e.target.value);
        }}
        hasError={hasError}
        onBlur={onBlur}
      />
    </>
  );
}
